import { useEffect, useMemo, useState } from 'react';
import { assignScopedRole, searchUsers } from '../../../services/roleAssignmentsService';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 10;

const ADMIN_LABEL_BY_ENTITY = {
  organization: 'Organization Admin',
  club: 'Club Admin',
  community: 'Community Admin',
};

const ENTITY_LABELS = {
  organization: 'Organization',
  club: 'Club',
  community: 'Community',
};

const DEFAULT_ROLE_BY_ENTITY = {
  organization: 'organization_admin',
  club: 'club_admin',
  community: 'community_admin',
};

const ROLE_LABEL_BY_KEY = {
  organization_admin: 'Organization Admin',
  club_admin: 'Club Admin',
  community_admin: 'Community Admin',
  tournament_admin: 'Tournament Admin',
  department_admin: 'Department Admin',
  team_admin: 'Department Admin',
};

const AssignRoleModal = ({
  isOpen,
  onClose,
  token,
  entityType,
  entityId,
  role,
  roleOptions = [],
  scope,
  onAssigned,
}) => {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [results, setResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState('');
  const [selectedRoleKey, setSelectedRoleKey] = useState('');

  const normalizedRoleOptions = useMemo(
    () => (Array.isArray(roleOptions)
      ? roleOptions.map((option) => ({
        ...option,
        roleKey: String(option.role || option.key || '').trim().toLowerCase(),
      })).filter((option) => option.roleKey)
      : []),
    [roleOptions],
  );

  const hasFixedRole = Boolean(role);
  const hasRoleSelection = !hasFixedRole && normalizedRoleOptions.length > 1;
  const firstRoleOptionKey = normalizedRoleOptions[0]?.key || '';
  const selectedRoleOption = normalizedRoleOptions.find((option) => option.key === selectedRoleKey) || null;
  const activeRole = selectedRoleOption?.roleKey || role || DEFAULT_ROLE_BY_ENTITY[entityType] || '';

  const adminLabel = selectedRoleOption?.label
    || ROLE_LABEL_BY_KEY[activeRole]
    || ROLE_LABEL_BY_KEY[role]
    || ADMIN_LABEL_BY_ENTITY[entityType]
    || 'Admin';
  const entityLabel = ENTITY_LABELS[entityType] || 'Entity';

  const canSearch = debouncedQ.trim().length >= 2;

  useEffect(() => {
    if (!isOpen) return;
    setQ('');
    setDebouncedQ('');
    setResults([]);
    setSelectedUser(null);
    setPage(1);
    setPagination({ page: 1, limit: PAGE_SIZE, total: 0 });
    setIsLoading(false);
    setIsAssigning(false);
    setError('');
    setSelectedRoleKey(hasRoleSelection ? '' : firstRoleOptionKey);
  }, [firstRoleOptionKey, hasRoleSelection, isOpen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQ(q.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    if (!isOpen || !token) return;

    if (!canSearch) {
      setResults([]);
      setPagination({ page: 1, limit: PAGE_SIZE, total: 0 });
      setError('');
      return;
    }

    let cancelled = false;

    const loadUsers = async () => {
      try {
        setIsLoading(true);
        setError('');
        const response = await searchUsers({ token, email: debouncedQ, page, limit: PAGE_SIZE, scope });
        if (cancelled) return;

        const items = response.items || [];
        const total = Number(response.total ?? response.totalItems ?? 0);

        setResults(items);
        setPagination({
          page: Number(response.page || page),
          limit: Number(response.limit || PAGE_SIZE),
          total,
        });
      } catch (err) {
        if (!cancelled) {
          setResults([]);
          setError(err.message || 'Unable to search users.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadUsers();

    return () => {
      cancelled = true;
    };
  }, [canSearch, debouncedQ, isOpen, page, scope, token]);

  const totalPages = useMemo(() => {
    const total = pagination.total || 0;
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
  }, [pagination.total]);

  const handleAssign = async () => {
    if (!selectedUser) return;

    try {
      setIsAssigning(true);
      setError('');

      const response = await assignScopedRole({
        token,
        userId: selectedUser.id,
        role: activeRole,
        scope: scope || {
          entityType,
          entityId,
        },
      });

      onAssigned?.(response);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth-roles-updated', { detail: response }));
      }
      onClose?.();
    } catch (err) {
      setError(err.message || `Unable to assign ${adminLabel}.`);
    } finally {
      setIsAssigning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.35)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title">Assign {adminLabel}</h5>
              <p className="text-muted small mb-0">
                Search user by email and assign as {adminLabel} for this {entityLabel}
              </p>
            </div>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>

          <div className="modal-body d-grid gap-3">
            {!selectedUser ? (
              <>
                {hasRoleSelection && !selectedRoleOption ? (
                  <div className="d-grid gap-2">
                    <p className="text-muted small mb-0">Choose admin type to continue:</p>
                    {normalizedRoleOptions.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        className="btn btn-outline-primary text-start"
                        onClick={() => setSelectedRoleKey(option.key)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <>
                    <input
                      className="form-control"
                      placeholder="Search by email…"
                      value={q}
                      onChange={(event) => setQ(event.target.value)}
                    />

                    {q.trim().length > 0 && q.trim().length < 2 && (
                  <div className="text-muted small">Enter at least 2 characters to search.</div>
                    )}

                    {isLoading ? (
                  <div className="text-muted small">Searching users…</div>
                    ) : canSearch && results.length === 0 ? (
                  <div className="text-muted small">No users found.</div>
                    ) : (
                  <div className="list-group">
                    {results.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                        onClick={() => setSelectedUser(user)}
                      >
                        <div className="d-flex align-items-center gap-2 text-start">
                          {user.avatarUrl ? (
                            <img
                              src={user.avatarUrl}
                              alt={user.name || 'User avatar'}
                              className="rounded-circle"
                              style={{ width: 32, height: 32, objectFit: 'cover' }}
                            />
                          ) : (
                            <div
                              className="rounded-circle bg-light border"
                              style={{ width: 32, height: 32 }}
                              aria-hidden="true"
                            />
                          )}
                          <div>
                            <div className="fw-semibold">{user.name || '—'}</div>
                            <div className="text-muted small">{user.email || '—'}</div>
                          </div>
                        </div>
                        <span className="btn btn-sm btn-outline-primary">Select</span>
                      </button>
                    ))}
                  </div>
                    )}

                    {canSearch && pagination.total > PAGE_SIZE && (
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      Page {page} of {totalPages}
                    </small>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={page <= 1 || isLoading}
                        onClick={() => setPage((current) => Math.max(1, current - 1))}
                      >
                        Prev
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={page >= totalPages || isLoading}
                        onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <div className="border rounded p-3 d-flex justify-content-between align-items-start">
                <div>
                  {hasRoleSelection && (
                    <div className="text-muted small">{adminLabel}</div>
                  )}
                  <p className="small text-muted mb-1">Selected User</p>
                  <div className="fw-semibold">{selectedUser.name || '—'}</div>
                  <div className="text-muted small">{selectedUser.email || '—'}</div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-link"
                  onClick={() => {
                    setSelectedUser(null);
                  }}
                >
                  Change
                </button>
              </div>
            )}

            {error && <div className="alert alert-danger py-2 mb-0">{error}</div>}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={isAssigning}>Cancel</button>
              <button
              type="button"
              className="btn btn-primary"
              onClick={handleAssign}
              disabled={!selectedUser || isAssigning || !activeRole}
            >
              {isAssigning ? 'Assigning…' : `Assign ${adminLabel}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignRoleModal;
